/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 화일명 : BackendStatusBadge.tsx
// 백엔드 서버 연결상태 표시 배지(초록 점 깜빡임 = 정상, 빨강 = 연결끊김) - 공통 컴포넌트
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { useRef, useState } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import { useBackendStatus } from '../hooks/useBackendStatus';
import { useCommonTranslation } from '../translations';
import { API_BASE_URL } from '../config';
import '../styles/backendStatusBadge.css';

interface BackendStatusBadgeProps {
  className?: string;
  clickable?: boolean; // true면 클릭시 API 서버 상태 팝업을 띄워줌
  hideLabel?: boolean; // true면 배지의 "Running/연결 끊김" 글자를 숨김(점 표시만 남김) - 팝업 안 배지는 항상 그대로 표시
  solutionTitle?: string; // mainFrame이 apiMainMenuTree로 받은 .env(SOLUTION_TITLE) 값 - 없으면 기본값 xGA(backend/index.ts와 동일 fallback)
}

// API_BASE_URL(예: http://localhost:5000)에서 포트만 추출 - 팝업의 "PORT xxxx" 표시용
const getPort = (): string => {
  try {
    return new URL(API_BASE_URL).port || '80';
  } catch {
    return '';
  }
};

const BackendStatusBadge: React.FC<BackendStatusBadgeProps> = ({ className, clickable, hideLabel, solutionTitle }) => {
  const online = useBackendStatus();
  const { ddKey } = useCommonTranslation();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const badgeRef = useRef<HTMLSpanElement>(null);

  const handleOpen = () => {
    const rect = badgeRef.current?.getBoundingClientRect();
    // 팝업의 우측 끝이 배지(표시)의 우측 끝과 맞춰지도록 배지 우측 x좌표를 anchor.left로 사용(paper에서 translateX(-100%) 적용)
    setAnchor(rect ? { top: rect.bottom, left: rect.right } : { top: 60, left: window.innerWidth - 24 });
    setOpen(true);
  };

  const isNarrow = window.innerWidth < 640;   // 앱의 모바일 분기점(640px)과 맞춤
  return (
    <>
      <span
        ref={badgeRef}
        className={`backend-status-badge ${online ? 'online' : 'offline'} ${clickable ? 'clickable' : ''} ${className || ''}`}
        title={clickable ? (ddKey('lblBackendStatusPageTooltip') || 'API 서버 상태를 확인합니다') : undefined}
        onClick={clickable ? handleOpen : undefined}
      >
        <span className="backend-status-dot"></span>
        {!hideLabel && (online ? (ddKey('lblBackendRunning') || 'Running') : (ddKey('lblBackendOffline') || '연결 끊김'))}
      </span>
      {/* 클릭 시 백엔드 루트(index.ts)가 새 창으로 보여주던 상태페이지를, 화면 이동 없이 동일한 정보를 배지 우측정렬 팝업으로 표시 */}
      {clickable && (
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          slotProps={{
            paper: {
              sx: isNarrow
                ? {
                    // 좁은 화면(모바일): 배지 위치와 상관없이 화면 가운데에 배치
                    position: 'fixed',
                    top: (anchor?.top ?? 60) + 8,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    m: 0,
                    width: 'min(320px, calc(100vw - 32px))',
                  }
                : {
                    position: 'fixed',
                    top: (anchor?.top ?? 60) + 8,
                    left: anchor?.left ?? window.innerWidth - 24,
                    transform: 'translateX(-100%)', // anchor.left(배지 우측 x좌표)를 팝업의 우측 끝으로 사용 -> 우측정렬
                    m: 0,
                    width: 320,
                  },
            },
          }}
        >
          <DialogContent sx={{ textAlign: 'center', py: 4 }}>
            {/* 로그인 화면(LoginGainsBrand)의 GAINS 로고와 동일한 마크(G+AI강조+NS) - 그 컴포넌트가 .gl-root 스코프
                CSS(loginGainsBrand.css)에 의존해서 그대로 가져다 쓸 수 없어 같은 색상값을 인라인으로 재현함 */}
            <div style={{ margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontFamily: '"Bricolage Grotesque","IBM Plex Sans KR",system-ui,sans-serif', fontWeight: 800, fontSize: 32, letterSpacing: '.02em', color: '#13233F' }}>
                G<b style={{ background: '#E9A417', color: '#13233F', padding: '0 4px', borderRadius: 4 }}>AI</b>NS
              </span>
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 6, color: '#1e293b' }}>{solutionTitle || ''} API Server</div>
            <div style={{ fontSize: 14, color: '#64748b', marginBottom: 18 }}>Express · TypeScript · Node.js</div>
            <span className={`backend-status-badge ${online ? 'online' : 'offline'}`} style={{ display: 'inline-flex' }}>
              <span className="backend-status-dot"></span>
              {online ? (ddKey('lblBackendRunning') || 'Running') : (ddKey('lblBackendOffline') || '연결 끊김')}
            </span>
            <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#94a3b8' }}>
              <span>PORT {getPort()}</span>
              <span>{new Date().toLocaleString('ko-KR')}</span>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default BackendStatusBadge;
